'use client';

import { Box, Link, Typography } from '@mui/material';
import Image from 'next/image';
import { useCallback, useState, type MouseEvent } from 'react';
import {
  InfographicComponentVariant as InfographicComponentVariantValue,
  VARIANT_COMPONENT_MAP,
  VARIANT_HIGH_YIELD_LABEL_MAP,
  VARIANT_TEST_ID_MAP,
  VARIANT_TITLE_MAP,
} from './config';
import type { InfographicComponentVariant as InfographicComponentVariantType } from './config';
import MoreInfoDialog from './MoreInfoDialog';

export const InfographicComponentVariant = InfographicComponentVariantValue;
export type InfographicComponentVariant = InfographicComponentVariantType;

type HighYieldSavingsInfographicProps = {
  variant?: InfographicComponentVariantType;
};

export const HighYieldSavingsInfographic = ({
  variant = InfographicComponentVariantValue.hysa_infographic,
}: HighYieldSavingsInfographicProps) => {
  const [openInfoDialog, setOpenInfoDialog] = useState(false);
  const DialogContentComponent = VARIANT_COMPONENT_MAP[variant];
  const title = VARIANT_TITLE_MAP[variant];
  const highYieldLabel = VARIANT_HIGH_YIELD_LABEL_MAP[variant];
  const testId = VARIANT_TEST_ID_MAP[variant];

  const handleOpenInfoDialog = useCallback((event: MouseEvent) => {
    event.preventDefault();
    setOpenInfoDialog(true);
  }, []);

  const handleCloseInfoDialog = useCallback(() => {
    setOpenInfoDialog(false);
  }, []);

  return (
    <Box
      data-testid={testId}
      sx={{
        borderRadius: '14px',
        backgroundColor: 'common.white',
        border: '1px solid',
        borderColor: 'neutral.lighter',
        boxShadow: 'none',
        padding: 0,
        maxWidth: '400px',
        minWidth: '350px',
      }}
    >
      <Typography
        variant="textSmallBold"
        component="p"
        sx={{
          textAlign: 'left',
          whiteSpace: 'pre-wrap',
          padding: '16px 16px 0px 16px',
          fontWeight: 600,
        }}
      >
        {title}
      </Typography>
      <Typography
        variant="textTiny"
        component="p"
        sx={{
          textAlign: 'left',
          whiteSpace: 'pre-wrap',
          padding: '0px 16px 16px 16px',
        }}
      >
        Earnings after 5 years, $10K initial deposit.{' '}
        <Link
          component="button"
          variant="textTiny"
          sx={{
            cursor: 'pointer',
            color: 'blue.light',
            border: 0,
            background: 'none',
            padding: 0,
            font: 'inherit',
          }}
          type="button"
          onClick={handleOpenInfoDialog}
        >
          Learn More.
        </Link>
        <MoreInfoDialog
          id={variant}
          content={<DialogContentComponent />}
          open={openInfoDialog}
          onClose={handleCloseInfoDialog}
          title={title}
        />
      </Typography>
      <Box
        sx={{
          position: 'relative',
        }}
      >
        <Image
          src="https://www.nerdwallet.com/cdn/banking/hysa-infographic/HYSA_Treatment2_DT.svg"
          style={{
            width: '100%',
            height: 'auto',
            objectFit: 'contain',
          }}
          width={341}
          height={225}
          priority={true}
          alt="Projection"
        />
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-around' }}>
        <Box
          sx={{
            display: 'flex',
            padding: '1rem 0.5rem 1rem 0.5rem',
            flexDirection: 'row',
            gap: 1,
            justifyContent: 'space-evenly',
          }}
        >
          <Box
            aria-hidden="true"
            sx={{
              width: '10px',
              height: '10px',
              flexShrink: 0,
              backgroundColor: 'blues.main',
              margin: '0.2rem 0 0 0',
            }}
          />
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <Typography variant="textTiny">Regular savings account</Typography>
            <Typography
              variant="textTiny"
              sx={{
                color: 'neutral.darker',
              }}
            >
              APY 0.41%
            </Typography>
          </Box>
        </Box>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            padding: '1rem 0.5rem 1rem 0.5rem',
            justifyContent: 'space-evenly',
            gap: 1,
          }}
        >
          <Box
            aria-hidden="true"
            sx={{
              width: '10px',
              height: '10px',
              flexShrink: 0,
              backgroundColor: 'greens.main',
              margin: '0.2rem 0 0 0',
            }}
          />
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <Typography
              variant="textTiny"
              sx={{
                flexDirection: 'column',
              }}
            >
              {highYieldLabel}
            </Typography>
            <Typography
              variant="textTiny"
              sx={{
                color: 'neutral.darker',
              }}
            >
              APY 4.00%
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
